import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CardMedia } from "@/components/CardImage";
import { AvailabilityFormDialog } from "@/components/AvailabilityFormDialog";
import { ConditionBadge, Tag } from "@/components/Badges";
import { ReportDialog } from "@/components/ReportDialog";
import { Loading, EmptyState, ErrorState } from "@/components/States";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SlidersHorizontal, Search, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const GAMES = [["", "Tutti i giochi"], ["pokemon", "Pokémon"], ["one_piece", "One Piece"]];
const CONDS = ["Mint", "Near Mint", "Excellent", "Light Played", "Played", "Poor"];
const LANGS = ["IT", "EN", "JP", "FR", "DE", "ES"];

function FilterSelect({ label, value, onChange, options, placeholder, testid }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-[#2DD4BF]">{label}</label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger className="bg-[#0B1020] border-[#2A3655]" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="max-h-64 bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">
          <SelectItem value="all">{placeholder || "Tutti"}</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Filters({ f, set, exps, rarities }) {
  return (
    <div className="space-y-4" data-testid="explore-filters">
      <FilterSelect label="Gioco" value={f.game} onChange={(v) => set({ ...f, game: v, expansion_id: "", rarity: "" })}
        options={GAMES.filter(([v]) => v).map(([value, label]) => ({ value, label }))} placeholder="Tutti i giochi" testid="filter-game" />
      <FilterSelect label="Espansione" value={f.expansion_id} onChange={(v) => set({ ...f, expansion_id: v })}
        options={exps.map((e) => ({ value: e.id, label: e.code ? `${e.name} (${e.code})` : e.name }))} placeholder="Tutte le espansioni" testid="filter-expansion" />
      <FilterSelect label="Rarità" value={f.rarity} onChange={(v) => set({ ...f, rarity: v })}
        options={rarities.map((r) => ({ value: r, label: r }))} placeholder="Tutte le rarità" testid="filter-rarity" />
      <FilterSelect label="Condizione" value={f.condition} onChange={(v) => set({ ...f, condition: v })}
        options={CONDS.map((c) => ({ value: c, label: c }))} placeholder="Qualsiasi" testid="filter-condition" />
      <FilterSelect label="Lingua" value={f.language} onChange={(v) => set({ ...f, language: v })}
        options={LANGS.map((l) => ({ value: l, label: l }))} placeholder="Qualsiasi" testid="filter-language" />
    </div>
  );
}

export default function Explore() {
  const { user } = useAuth();
  const [f, setF] = useState({ game: "", expansion_id: "", rarity: "", condition: "", language: "", q: "" });
  const [exps, setExps] = useState([]);
  const [rarities, setRarities] = useState([]);
  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isOwner = (a) => user && user.id && a.owner?.id === user.id;

  useEffect(() => {
    api.get("/catalog/expansions", { params: { game: f.game || undefined } }).then((r) => setExps(r.data)).catch(() => setExps([]));
    api.get("/catalog/rarities", { params: { game: f.game || undefined } }).then((r) => setRarities(r.data)).catch(() => setRarities([]));
  }, [f.game]);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get("/explore", { params: {
        game: f.game || undefined, expansion_id: f.expansion_id || undefined, rarity: f.rarity || undefined,
        condition: f.condition || undefined, language: f.language || undefined, q: f.q || undefined } });
      setState({ loading: false, error: null, items: data.items });
    } catch { setState({ loading: false, error: "Impossibile caricare gli scambi.", items: [] }); }
  }, [f]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (a) => { setEditing(a); setDialogOpen(true); };
  const remove = async (a) => {
    if (!window.confirm(`Eliminare l'annuncio "${a.card?.name}"? L'operazione non è reversibile.`)) return;
    try { await api.delete(`/availabilities/${a.id}`); toast.success("Annuncio eliminato."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#F8FAFC] sm:text-4xl">Esplora gli scambi</h1>
          <p className="mt-2 text-[#CBD5E1]">Carte pubblicate dai collezionisti e disponibili allo scambio. Registrati per proporre uno scambio.</p>
        </div>
        {user && user.id && (
          <Button onClick={openNew} disabled={!user.email_verified} data-testid="explore-add-card" className="bg-[#7C3AED] hover:bg-[#6D28D9]">
            <Plus className="mr-2 h-4 w-4" />Nuovo annuncio
          </Button>
        )}
      </div>

      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-[#94A3B8]" />
          <Input data-testid="explore-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Cerca una carta…" className="bg-[#141B2D] border-[#2A3655] pl-9" />
        </div>
        <Sheet>
          <SheetTrigger asChild><Button variant="outline" className="border-[#2A3655] bg-[#141B2D] text-[#F8FAFC] lg:hidden" data-testid="open-filters"><SlidersHorizontal className="mr-2 h-4 w-4" />Filtri</Button></SheetTrigger>
          <SheetContent side="left" className="overflow-y-auto bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">
            <SheetHeader><SheetTitle className="text-[#F8FAFC]">Filtri</SheetTitle></SheetHeader>
            <div className="mt-6"><Filters f={f} set={setF} exps={exps} rarities={rarities} /></div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block"><div className="sticky top-24 rounded-xl border border-[#2A3655] bg-[#141B2D] p-5"><Filters f={f} set={setF} exps={exps} rarities={rarities} /></div></aside>
        <div>
          {state.loading ? <Loading /> : state.error ? <ErrorState message={state.error} onRetry={load} /> :
            state.items.length === 0 ? (
              <EmptyState title="Nessuna carta disponibile" description="Al momento non ci sono carte che corrispondono ai filtri. Prova a modificarli o torna più tardi." />
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4" data-testid="explore-grid">
                {state.items.map((a) => (
                  <div key={a.id} className="card-lift rounded-xl border border-[#2A3655] bg-[#141B2D] p-2" data-testid="explore-card">
                    <CardMedia photos={a.photos} card={a.card} />
                    <div className="mt-2 space-y-1 px-1 pb-1">
                      <p className="line-clamp-1 text-sm font-semibold text-[#F8FAFC]">{a.card?.name}</p>
                      <p className="text-xs text-[#94A3B8]">{a.card?.expansion_name || a.card?.expansion_code || "—"}{a.card?.rarity ? ` · ${a.card.rarity}` : ""}</p>
                      <div className="flex flex-wrap items-center gap-1 pt-1"><ConditionBadge value={a.condition} /><Tag>{a.language}</Tag></div>
                      <div className="flex items-center justify-between pt-1.5">
                        <Link to={`/collezionista/${a.owner?.username}`} className="text-xs text-[#2DD4BF] hover:underline" data-testid="explore-owner">@{a.owner?.username}</Link>
                        {isOwner(a) ? <Tag tone="#2DD4BF">Tuo</Tag> : <ReportDialog targetType="availability" targetId={a.id} label="" />}
                      </div>
                      {isOwner(a) && (
                        <div className="flex items-center gap-1 pt-1">
                          <button onClick={() => openEdit(a)} data-testid="explore-edit-card" className="flex items-center gap-1 rounded px-2 py-1 text-xs text-[#CBD5E1] hover:bg-[#1C2640]"><Pencil className="h-3.5 w-3.5" />Modifica</button>
                          <button onClick={() => remove(a)} data-testid="explore-delete-card" className="flex items-center gap-1 rounded px-2 py-1 text-xs text-[#CBD5E1] hover:bg-[#EF4444]/20 hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" />Elimina</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>

      <AvailabilityFormDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={load} />
    </div>
  );
}
